import useSettingsPanel from '@Guidelines/Shell/AppShell/Hooks/useSettingsPanel';
import NavigationMenu from '@Guidelines/Shell/AppShell/Menu/NavigationMenu/NavigationMenu';
import { theme } from '@Guidelines/Theme/theme';
import { SETTINGS_SECTION_IDS } from '@UI/Routing/navigationMenu';
import { css } from '@emotion/react';
import * as React from 'react';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';

export const SETTINGS_PANEL_ID = 'settings-panel';
export const SETTINGS_TOGGLE_ID = 'settings-toggle';

const TITLE_ID = 'settings-panel-title';

const maskStyle = css`
	position: absolute;
	inset: 0;
	z-index: 1;
	background-color: color-mix(in srgb, ${theme.colors.ink} 40%, transparent);

	@media (max-width: ${theme.layout.compactBreakpoint}) {
		display: none;
	}
`;

const panelStyle = css`
	display: flex;
	flex-direction: column;
	width: ${theme.layout.settingsPanelWidth};
	background-color: ${theme.colors.surface};
	border-right: 1px solid ${theme.colors.border};

	@media (max-width: ${theme.layout.compactBreakpoint}) {
		display: none;
	}
`;

/** A column of the row: it takes its own width and the page beside it takes the rest. */
const pinnedStyle = css`
	flex-shrink: 0;
`;

/** Taken out of the row, so nothing behind it moves; closed it waits off-screen to slide in from the left. */
const floatingStyle = css`
	position: absolute;
	top: 0;
	bottom: 0;
	left: 0;
	z-index: 2;
	transform: translateX(-100%);
	visibility: hidden;
	transition:
		transform ${theme.motion.duration},
		visibility 0s linear ${theme.motion.duration};

	@media (prefers-reduced-motion: reduce) {
		transition: none;
	}
`;

const overlayStyle = css`
	transform: translateX(0);
	visibility: visible;
	transition:
		transform ${theme.motion.duration},
		visibility 0s;

	@media (prefers-reduced-motion: reduce) {
		transition: none;
	}
`;

const titleStyle = css`
	${theme.typography.cardTitle}
	margin: 0;
	padding: 1.25rem 1.25rem 0.5rem;
	color: ${theme.colors.ink};
`;

const contentStyle = css`
	flex: 1;
	min-height: 0;
	overflow-y: auto;
	padding: 0.5rem 0.75rem 1.5rem;
`;

/**
 * Settings navigation on wide viewports, mounted in the shell's body row so its top edge is the bottom of
 * the header. `pinned` (settings routes): a column of the row with no mask, the page sits beside it.
 * `overlay` (opened from the toggle elsewhere): slides over the page behind a mask; `Escape` or a click on
 * the mask closes it and hands the focus back to the toggle. `closed`: hidden and `inert`.
 */
const SettingsPanel: React.FC = () => {
	const { mode, close } = useSettingsPanel();
	const isOverlay = mode === 'overlay';

	const handleDismiss = React.useCallback(() => {
		close();
		document.getElementById(SETTINGS_TOGGLE_ID)?.focus();
	}, [close]);

	React.useEffect(() => {
		if (!isOverlay) {
			return undefined;
		}

		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				handleDismiss();
			}
		};

		document.addEventListener('keydown', handleKeyDown);

		return () => {
			document.removeEventListener('keydown', handleKeyDown);
		};
	}, [isOverlay, handleDismiss]);

	return (
		<React.Fragment>
			<ConditionalLoader data={isOverlay}>
				<div
					css={maskStyle}
					onClick={handleDismiss}
					aria-hidden="true"
					data-testid="settings-panel-mask"
				/>
			</ConditionalLoader>
			<aside
				id={SETTINGS_PANEL_ID}
				css={[
					panelStyle,
					mode === 'pinned' ? pinnedStyle : floatingStyle,
					isOverlay ? overlayStyle : null,
				]}
				aria-labelledby={TITLE_ID}
				inert={mode === 'closed'}
			>
				<h2 id={TITLE_ID} css={titleStyle}>
					Paramètres
				</h2>
				<div css={contentStyle}>
					<NavigationMenu sectionIds={SETTINGS_SECTION_IDS} ariaLabel="Paramètres" />
				</div>
			</aside>
		</React.Fragment>
	);
};

export default SettingsPanel;
